import { calculateLoanToValueRatio } from "../../../loan";
import { deepClone, roundWithFloatingErrorCheck, sum } from "../../../utils";
const MONTHS_IN_YEAR = 12;
/**
 * Calculates amortization rate based on swedish amortization requirements for loan-to-value ratio
 *
 * @param housingValue Housing value
 * @param mortgageAmount Mortgage amount
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Mortgage amortization rate
 */
export function getAmortizationRateByLoanToValueRequirement(housingValue, mortgageAmount, localizationParameters) {
    const { highAmortizationRate, lowAmortizationRate, highLoanToValueThreshold, lowLoanToValueThreshold } = localizationParameters;
    const ltv = calculateLoanToValueRatio(housingValue, mortgageAmount);
    let minAmortizationRate = 0;
    if (ltv > highLoanToValueThreshold) {
        minAmortizationRate = highAmortizationRate;
    }
    else if (ltv > lowLoanToValueThreshold) {
        minAmortizationRate = lowAmortizationRate;
    }
    return minAmortizationRate;
}
/**
 * Calculates amortization rate based on swedish mortgage amortization regulations for debt-to-income
 *
 * @param mortgageAmount Mortgage amount
 * @param highestHouseholdSalaryIncome Highest household salary income during forecast so far
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Debt-to-income based amortization rate
 */
export function getAmortizationRateByDebtToIncome(mortgageAmount, highestHouseholdSalaryIncome, localizationParameters) {
    const yearlyIncome = highestHouseholdSalaryIncome * MONTHS_IN_YEAR;
    const { amortizationDebtToIncomeFactor, incomeBasedAmortizationRate } = localizationParameters;
    if (mortgageAmount > yearlyIncome * amortizationDebtToIncomeFactor) {
        return incomeBasedAmortizationRate;
    }
    return 0;
}
/**
 * Distributes the total amortization to the mortgage loans, first by last month amortizations distribution ratio, if loan amount is available,
 * secondly by loan with highest interest.
 *
 * @param mortgage Array with all loans in mortgage after last month, including loan amount, interest rate and monthly amortization
 * @param totalMonthlyAmortization Sum of mortgages monthly amortization
 * @returns Monthly amortization amounts in an array in same order as mortgage in input param
 */
export function distributeAmortization(mortgage, totalMonthlyAmortization) {
    const mortgageClone = deepClone(mortgage);
    const nrOfLoans = mortgageClone.length;
    const mortgageAmount = sum(mortgageClone, (loan) => loan.amount);
    const calculatedAmortization = new Array(nrOfLoans).fill(0);
    if (nrOfLoans <= 0 || mortgageAmount <= 0) {
        return calculatedAmortization;
    }
    const lastMonthAmortizationUnpaidLoans = mortgageClone.filter((loan) => loan.amount > 0).reduce((acc, loan) => acc + loan.monthlyAmortization, 0);
    let amortizationRest = 0;
    // If mortgage has user defined amortization > 0 or last month amortization > 0, the new total amortization
    // will be distributed in the same amortization ratio.
    if (lastMonthAmortizationUnpaidLoans > 0) {
        mortgageClone.forEach((loan, idx) => {
            const maxLoanAmortizationNotRounded = totalMonthlyAmortization * (loan.monthlyAmortization / lastMonthAmortizationUnpaidLoans);
            const maxLoanAmortization = roundWithFloatingErrorCheck(maxLoanAmortizationNotRounded, Math.ceil);
            const maxPossibleLoanAmortization = Math.min(maxLoanAmortization, loan.amount);
            if (maxLoanAmortization > loan.amount) {
                amortizationRest = amortizationRest + maxLoanAmortization - maxPossibleLoanAmortization;
            }
            calculatedAmortization[idx] = maxPossibleLoanAmortization;
            // eslint-disable-next-line no-param-reassign
            loan.amount = Math.max(loan.amount - maxPossibleLoanAmortization, 0);
        });
    }
    else {
        amortizationRest = totalMonthlyAmortization;
    }
    // If any of the loan amounts in the mortgage is smaller than the expected amortization,
    // the rest of the amortization will be distributed to the loan with highest interest.
    while (amortizationRest > 0) {
        const unPaidLoans = mortgageClone.filter((loan) => loan.amount > 0);
        if (unPaidLoans.length <= 0) {
            break;
        }
        const loanWithHighestInterest = unPaidLoans.reduce((prev, next) => (next.interestRate > prev.interestRate ? next : prev));
        const index = mortgageClone.findIndex((loan) => loan === loanWithHighestInterest);
        const monthlyAmortization = Math.min(mortgageClone[index].amount, amortizationRest);
        calculatedAmortization[index] += monthlyAmortization;
        mortgageClone[index].amount -= monthlyAmortization;
        amortizationRest -= monthlyAmortization;
    }
    return calculatedAmortization;
}
/**
 * Calculates amortization amounts & rate by by swedish mortgage market regulations.
 *
 * @param housingValue Housing value
 * @param mortgageAmount Array of mortgage data
 * @param amortizationRequirementHistory Relevant amortization rate history, loan amount at rate change and highest adults income so far
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @param additionalMortgage Array of mortgage data from additional housing
 * @returns Mortgage amortization rates, total monthly amortization and
 * monthly amortization on every loan in the same order as mortgage in input param
 */
export function calculateAmortizationWithMarketRequirements(housingValue, mortgage, amortizationRequirementHistory, localizationParameters, additionalMortgage, customLoanToValueAmortizationRate) {
    const { highAmortizationRate, lowAmortizationRate, highLoanToValueThreshold, lowLoanToValueThreshold } = localizationParameters;
    const mortgageAmount = sum(mortgage, (loan) => loan.amount);
    const additionalMortgageAmount = additionalMortgage ? sum(additionalMortgage, (loan) => loan.amount) : 0;
    const currentLoanToValueAmortizationRate = customLoanToValueAmortizationRate !== null && customLoanToValueAmortizationRate !== void 0 ? customLoanToValueAmortizationRate : getAmortizationRateByLoanToValueRequirement(housingValue, mortgageAmount, localizationParameters);
    const currentDebtToIncomeAmortizationRate = getAmortizationRateByDebtToIncome(mortgageAmount + additionalMortgageAmount, amortizationRequirementHistory.highestHouseholdSalaryIncome, localizationParameters);
    const lastMonthAmortization = mortgage.reduce((acc, loan) => acc + loan.monthlyAmortization, 0);
    let totalAmortization;
    // Calculates new mortgage amount at amortization requirement change.
    // New amount is based on loan-to-value threshold greater than or equal to curren loan-to-value at change.
    // If loan-to-value > highest threshold, the amortization is calculated from initial mortgage amount.
    if (currentLoanToValueAmortizationRate < amortizationRequirementHistory.loanToValueAmortizationRate) {
        let mortgageAmountAtRequirement = mortgageAmount;
        if (!customLoanToValueAmortizationRate && amortizationRequirementHistory.loanToValueAmortizationRate <= highAmortizationRate) {
            mortgageAmountAtRequirement =
                currentLoanToValueAmortizationRate >= lowAmortizationRate
                    ? housingValue * highLoanToValueThreshold
                    : housingValue * lowLoanToValueThreshold;
        }
        const amortizationRate = currentLoanToValueAmortizationRate + currentDebtToIncomeAmortizationRate;
        const amortizationNotRounded = (mortgageAmountAtRequirement * amortizationRate) / MONTHS_IN_YEAR;
        totalAmortization = roundWithFloatingErrorCheck(amortizationNotRounded, Math.ceil);
    }
    // Income based amortization installment is removed from amortization expenses.
    else if (currentDebtToIncomeAmortizationRate <= 0) {
        const amortizationNotRounded = (currentLoanToValueAmortizationRate * amortizationRequirementHistory.mortgageAmountAtRequirement) / MONTHS_IN_YEAR;
        totalAmortization = roundWithFloatingErrorCheck(amortizationNotRounded, Math.ceil);
    }
    // No change in amortization requirements.
    else {
        totalAmortization = lastMonthAmortization;
    }
    const calculatedAmortization = distributeAmortization(mortgage, totalAmortization);
    const totalMonthlyAmortization = calculatedAmortization.reduce((acc, amortization) => acc + amortization, 0);
    return {
        loanToValueAmortizationRate: currentLoanToValueAmortizationRate,
        debtToIncomeAmortizationRate: currentDebtToIncomeAmortizationRate,
        monthlyAmortization: calculatedAmortization,
        totalMonthlyAmortization,
    };
}
/**
 * Calculates amortization rate and monthly amortization expense from amortization requirements for LTV in swedish market.
 *
 * @param housingValue Housing value
 * @param mortgageAmount One mortgage loan amount
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Amortization rate and monthly amortization expense
 */
export function getMonthlyAmortizationWithLTVRequirement(housingValue, mortgageAmount, localizationParameters) {
    const amortizationRate = getAmortizationRateByLoanToValueRequirement(housingValue, mortgageAmount, localizationParameters);
    const yearAmortizationExpense = mortgageAmount * amortizationRate;
    const monthlyAmortization = roundWithFloatingErrorCheck(yearAmortizationExpense / MONTHS_IN_YEAR, Math.ceil);
    return {
        amortizationRate,
        monthlyAmortization,
    };
}
